import { useState, useEffect } from "react";

import { LoadingButton } from "@mui/lab";
import { add, format, isBefore } from "date-fns";

import { useIntakeStore } from "@/hooks/useIntakeStore";
// @mui
import {
  TextField,
  TextFieldProps,
  FormControl,
  FormHelperText,
  FormLabel,
  // Button,
  Stack,
  Typography,
  Box,
} from "@mui/material";
import { QuestionType } from "utils/booking";

// ----------------------------------------------------------------------

interface IProps {
  name: string;
  label: string;
  note?: string;
  // eslint-disable-next-line no-unused-vars
  onSelected: (value: string) => void;
  baseQuestion?: QuestionType;
  saving?: boolean;
  readOnly?: boolean;
  minYears?: number;
  grouped?: boolean;
  containedButton?: boolean;
  requiredMessage?: string;
}

export default function IntakeTextField({
  name,
  readOnly,
  label,
  fullWidth,
  onSelected,
  value,
  multiline,
  note,
  saving,
  minYears,
  grouped,
  containedButton,
  requiredMessage,
  ...other
}: IProps & TextFieldProps) {
  const [valueText, setValue] = useState((value as string) || "");
  const [error, setError] = useState(false);
  const { setData, ...intake } = useIntakeStore();

  useEffect(() => {
    setValue((value as string) || "");
  }, [value]);

  const handleSubmit = () => {
    if (
      minYears &&
      isBefore(
        new Date(valueText as string),
        new Date(add(new Date(), { years: minYears })),
      )
    ) {
      setError(true);
      return;
    }
    if (other?.required && valueText.trim() === "") {
      setError(true);
    } else {
      setError(false);
      onSelected(valueText);
    }
  };

  const handleGroup = (value: string) => {
    if (!grouped) return;
    setData({ ...intake, [name]: value });
  };

  return (
    <FormControl sx={{ width: "100%" }}>
      {!grouped ? (
        <FormLabel
          sx={{
            fontFamily: "JazminAlt",
            fontSize: "22px",
            textAlign: "center",
            lineHeight: "22px",
            mt: 2,
            mb: 4,
          }}
        >
          {label}
        </FormLabel>
      ) : (
        <FormLabel
          sx={{
            fontFamily: "JazminAlt",
            fontSize: "16px",
            lineHeight: "16px",
            mb: 2,
          }}
        >
          {label}
        </FormLabel>
      )}
      <TextField
        disabled={readOnly}
        fullWidth={fullWidth}
        onChange={(e) => {
          setValue(e.target.value);
          handleGroup(e.target.value);
        }}
        // variant={multiline ? 'outlined' : 'standard'}
        variant="outlined"
        value={valueText}
        error={error}
        multiline={multiline}
        inputProps={{ maxLength: 255 }}
        rows={5}
        {...other}
        sx={{
          "& .MuiInputLabel-root": {
            fontFamily: "SackersGothic",
          },
          "& .MuiOutlinedInput-input": {
            fontFamily: "ABCDiaTypeRegular",
          },
        }}
      />
      {note && (
        <FormHelperText sx={{ mb: 2 }}>
          <em>{note}</em>
        </FormHelperText>
      )}
      <Stack>
        {error && minYears && (
          <Typography variant="caption" color={"error"} sx={{ width: "100%" }}>
            {`The selected date is too old. The minimum date allowed is ${format(
              new Date(add(new Date(), { years: minYears })),
              "yyyy-MM-dd",
            )}`}
          </Typography>
        )}
        {error && !minYears && (
          <Typography variant="caption" color={"error"} sx={{ width: "100%" }}>
            {requiredMessage ?? `This Question is required`}
          </Typography>
        )}
        {!grouped ? (
          <Box>
            <LoadingButton
              sx={{
                my: 2,
                py: 1.5,
                textTransform: "capitalize",
                fontFamily: "ABCDiaTypeRegular",
                background: containedButton ? "#3d4732" : undefined,
                fontSize: "18px",
              }}
              variant={containedButton ? "contained" : "outlined"}
              fullWidth
              loading={saving}
              onClick={handleSubmit}
            >
              Next
            </LoadingButton>
          </Box>
        ) : null}
      </Stack>
    </FormControl>
  );
}
